import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

type PdfDoc = { info: Record<string, string> };

type PdfMakeDoc = {
  open: () => void;
  print: () => void;
  download: (filename: string) => void;
  getBase64: (cb: (data: string) => void) => void;
};

declare let pdfMake: {
  createPdf: (_doc: unknown) => PdfMakeDoc;
};

export type PdfAction = 'open' | 'print' | 'download';

/**
 * Render a pdfMake doc definition and surface it to the user.
 *
 * On the web, pdfMake's `open`/`print`/`download` use browser primitives
 * (blob URLs, the `download` attribute, the print dialog). None of those work
 * inside a Capacitor WKWebView, so on native platforms we render to base64,
 * persist the file to the cache directory, and hand it to the OS share sheet
 * (Save to Files, Mail, open in a PDF viewer, …).
 */
export function exportPdf(doc: PdfDoc, action: PdfAction): void {
  const pdf = pdfMake.createPdf(doc);

  if (!Capacitor.isNativePlatform()) {
    if (action === 'open') {
      pdf.open();
    } else if (action === 'print') {
      pdf.print();
    } else {
      pdf.download(doc.info['title']);
    }
    return;
  }

  const title = sanitizeFilename(doc.info['title'] || 'invoice');
  pdf.getBase64((data) => {
    void shareViaFilesystem(data, title, doc.info['title']);
  });
}

async function shareViaFilesystem(data: string, filename: string, shareTitle: string): Promise<void> {
  const { uri } = await Filesystem.writeFile({
    path: `${filename}.pdf`,
    data,
    directory: Directory.Cache,
  });
  // Use `files`, not `url`: on Android `url` is shared as plain text, so the
  // PDF must go through `files` to be attached. Works for both iOS and Android.
  await Share.share({ title: shareTitle, files: [uri] });
}

function sanitizeFilename(name: string): string {
  return name.replace(/[/\\?%*:|"<>]/g, '-').trim();
}
